import type { Callout } from '@tv-casino/viewmodels/lib/callouts.js';
import {
  admit,
  captionMs,
  nextDue,
  STALE_MS,
  type QueuedCall,
} from '@tv-casino/viewmodels/lib/voiceQueue.js';
import { audioGraph, duckAmbience, resume } from './sound.js';

/**
 * The TV dealer's voice: recorded clips from `public/sounds/voice/`, one call
 * at a time, through the same audio context and master fader as the cues.
 *
 * - **Queue.** One call speaks at a time. A `high` call clears everything
 *   below it, cutting one mid-word if it has to; a `low` call is dropped if
 *   anything is already waiting. A call older than `STALE_MS` when its turn
 *   comes is dropped: late is worse than silent.
 * - **Stitching.** A call's clips are scheduled back to back on the context
 *   clock with a short gap, never on timers, so they cannot drift apart.
 * - **Captions.** Every call that gets its turn is announced to the caption,
 *   spoken or not. With sound off, a clip still loading, or a file missing,
 *   the call is a caption alone — never a broken table.
 * - **Loading.** Only the clips the current game can call are decoded;
 *   decoded audio is big, and a cast device has little memory to spare.
 */

/** Silence between two stitched clips. */
const GAP_S = 0.04;
export { STALE_MS };
/** Full-scale voice level under the master fader. */
const VOICE_GAIN = 1;

/** What the caption hears: the call now speaking, and for how long. */
export interface Spoken {
  call: Callout;
  durationMs: number;
}

let wanted = new Set<string>();
const buffers = new Map<string, AudioBuffer>();
const loading = new Set<string>();
let volume = 1;
let voiceOut: { ctx: AudioContext; gain: GainNode } | null = null;

let queue: QueuedCall[] = [];
let current: { call: Callout; sources: AudioBufferSourceNode[]; timer: ReturnType<typeof setTimeout> } | null =
  null;
const listeners = new Set<(spoken: Spoken) => void>();

/** Hear every call as it gets its turn. Returns the unsubscribe. */
export function onSpoken(fn: (spoken: Spoken) => void): () => void {
  listeners.add(fn);
  return () => listeners.delete(fn);
}

/** The host's voice slider, 0–100. */
export function setVoiceVolume(percent: number): void {
  volume = Number.isFinite(percent) ? Math.min(1, Math.max(0, percent / 100)) : 1;
  if (!voiceOut) return;
  try {
    voiceOut.gain.gain.setTargetAtTime(VOICE_GAIN * volume, voiceOut.ctx.currentTime, 0.15);
  } catch {
    // A context that cannot automate keeps its old level.
  }
}

/**
 * The clips the table on screen can call. Anything else is let go — a game
 * swap drops the old game's buffers — and the new set loads in the
 * background once sound is on. Safe to call on every render.
 */
export function loadVoice(clips: readonly string[]): void {
  wanted = new Set(clips);
  for (const id of buffers.keys()) if (!wanted.has(id)) buffers.delete(id);
  fetchWanted();
}

function fetchWanted(): void {
  const graph = audioGraph();
  if (!graph || typeof fetch !== 'function') return;
  for (const id of wanted) {
    if (buffers.has(id) || loading.has(id)) continue;
    loading.add(id);
    void (async () => {
      try {
        const res = await fetch(`/sounds/voice/${id}.m4a`);
        if (!res.ok) return;
        const buffer = await graph.ctx.decodeAudioData(await res.arrayBuffer());
        if (wanted.has(id)) buffers.set(id, buffer);
      } catch {
        // A missing or undecodable clip costs its calls their voice, nothing more.
      } finally {
        loading.delete(id);
      }
    })();
  }
}

/** Queue one call; `voiceQueue` owns which calls wait, cut or are dropped. */
export function say(call: Callout, now = Date.now()): void {
  const decision = admit(queue, call, current?.call ?? null, now);
  queue = decision.queue;
  if (decision.cut) stop();
  if (decision.queued) pump();
}

function pump(): void {
  if (current) return;
  const due = nextDue(queue, Date.now());
  queue = due.queue;
  if (!due.call) {
    duckAmbience(false);
    return;
  }
  start(due.call);
}

function start(call: Callout): void {
  // Any call is a chance to pick up clips that could not load before sound was on.
  fetchWanted();
  const graph = audioGraph();
  const clips = call.clips.map((id) => buffers.get(id));
  const sources: AudioBufferSourceNode[] = [];
  let durationMs = captionMs(call.text);

  if (graph && clips.every((b): b is AudioBuffer => b !== undefined)) {
    try {
      void resume();
      const { ctx } = graph;
      const out = voiceGain(graph.ctx, graph.master);
      let at = ctx.currentTime + 0.02;
      const from = at;
      for (const buffer of clips) {
        const source = ctx.createBufferSource();
        source.buffer = buffer;
        source.connect(out);
        source.start(at);
        sources.push(source);
        at += buffer.duration + GAP_S;
      }
      durationMs = Math.round((at - from) * 1_000);
      duckAmbience(true);
    } catch {
      // The context refused; this call is a caption.
      sources.length = 0;
    }
  }

  current = { call, sources, timer: setTimeout(finish, durationMs) };
  for (const fn of listeners) fn({ call, durationMs });
}

function finish(): void {
  current = null;
  pump();
}

/** Cut the call speaking now. */
function stop(): void {
  if (!current) return;
  clearTimeout(current.timer);
  for (const source of current.sources) {
    try {
      source.stop();
    } catch {
      // Already finished.
    }
  }
  current = null;
}

function voiceGain(ctx: AudioContext, master: GainNode): GainNode {
  if (voiceOut?.ctx === ctx) return voiceOut.gain;
  const gain = ctx.createGain();
  gain.gain.value = VOICE_GAIN * volume;
  gain.connect(master);
  voiceOut = { ctx, gain };
  return gain;
}

/** Leaving the table: silence the dealer and forget whatever was waiting. */
export function hushVoice(): void {
  queue = [];
  stop();
  duckAmbience(false);
}
